import axios from "axios";
import {
  Box,
  Drawer,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  Switch,
  Typography,
} from "@mui/material";
import localize from "../../common/localize";
import NotificationItem from "./NotificationItem";
import { Close } from "@mui/icons-material";
import useGlobalContext from "../../hooks/useGlobalContext";
import { useEffect, useState } from "react";
import useSWR from "swr";

export default function NotificationPane({
  isOpen,
  setIsOpen,
  setNotificationCount,
  user,
  personalId,
}) {
  const {
    NOTIFICATION_ENDPOINT,
    getHeadersWithCorrespondingPagination,
    config,
    basicFilterData,
  } = useGlobalContext();

  const [notificationData, setNotificationData] = useState([]);
  const [showUnread, setShowUnread] = useState(false);

  const { data } = useSWR([
    `${NOTIFICATION_ENDPOINT}/${user}/${personalId ?? 0}`,
    getHeadersWithCorrespondingPagination(config, 0, basicFilterData),
  ]);

  useEffect(() => {
    if (data?.Data) {
      setNotificationData([...data?.Data]);
    }
  }, [data?.Data]);

  const noNotifications = () => {
    return (
      <NotificationItem
        notification={{
          type: 0,
          message: localize.no_notifications,
        }}
      />
    );
  };

  useEffect(() => {
    if (data?.Data) {
      setNotificationCount(data?.Data?.filter((x) => x.unread === true).length);
    }
  }, [data]);

  const markAsRead = async (notification) => {
    const index = notificationData.findIndex((x) => x.id === notification.id);
    if (index >= 0) {
      notificationData[index].unread = false;
      setNotificationData([...notificationData]);
    }

    const result = axios.put(
      `${NOTIFICATION_ENDPOINT}/${notification.Id}/read/${user}`,
      undefined,
      config,
    );
  };

  const notificationsCheckedChanged = (e) => {
    if (!e) {
      setShowUnread(false);
      return;
    }

    setShowUnread(e.target.checked);
  };

  const getNotificationsComponent = () => {
    const notifications = showUnread
      ? notificationData?.filter((x) => x.unread === showUnread)
      : [...notificationData];

    return notifications && notifications.length > 0
      ? notifications.map((notification) => (
          <Box key={notification.Id}>
            <NotificationItem
              notification={notification}
              onNotificationClick={markAsRead}
            />
          </Box>
        ))
      : noNotifications();
  };

  const getUnreadCount = () => {
    if (!notificationData || notificationData.length === 0) {
      return "";
    }

    return `(${notificationData?.filter((x) => x.unread === true).length})`;
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={() => setIsOpen(false)}
      className="flex flex-col gap-2 p-2"
      sx={{
        "& .MuiDrawer-paper": {
          width: "450px",
          maxWidth: "100%",
          height: "100%",
        },
      }}
    >
      <Box
        className="flex justify-between items-center p-4 menu_background_color"
        sx={{ borderBottom: "1px solid #ddd" }}
      >
        <Typography sx={{ color: "white" }}>
          {localize.notifications}
        </Typography>
        <IconButton sx={{ color: "white" }} onClick={() => setIsOpen(false)}>
          <Close />
        </IconButton>
      </Box>

      <Box className="p-4">
        <Box className="flex justify-between gap-2">
          <FormGroup>
            <FormControlLabel
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                },
              }}
              control={
                <Switch
                  checked={showUnread}
                  onChange={notificationsCheckedChanged}
                  slotprops={{ input: { "aria-label": "controlled" } }}
                />
              }
              label={`${
                localize.show_only_unread_notifications
              } ${getUnreadCount()}`}
            />
          </FormGroup>
        </Box>

        <List className="flex flex-col gap-2">
          {getNotificationsComponent()}
        </List>
      </Box>
    </Drawer>
  );
}
